import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MapPin, ChevronDown, ChevronRight, Loader2, AlertTriangle, Network, Truck,
  FileText, Ticket as TicketIcon, Building2, Radio,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/custom/StatusBadge';
import {
  Sheet, SheetTrigger, SheetContent, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { fetchTicketEnrichment, fetchOrderWorkflow } from '@/hooks/useTicketEnrichment';
import type { TicketEnrichmentResponse, OrderWorkflowEntry } from '@uniticket/shared';

type LoadState = 'idle' | 'loading' | 'error' | 'ready';
type WorkflowState = Record<number, OrderWorkflowEntry[] | 'loading' | 'error'>;

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const v = (x: unknown): string => (x === null || x === undefined || x === '' ? '—' : String(x));

export function AddressEnrichmentPanel({ ticketId, jvid }: { ticketId: string; jvid?: number | null }) {
  const { t } = useTranslation(['tickets', 'common']);
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<LoadState>('idle');
  const [data, setData] = useState<TicketEnrichmentResponse | null>(null);
  const [workflows, setWorkflows] = useState<WorkflowState>({});

  useEffect(() => {
    if (!open || state !== 'idle') return;
    setState('loading');
    fetchTicketEnrichment(ticketId, jvid)
      .then((res) => { setData(res.data); setState('ready'); })
      .catch(() => setState('error'));
  }, [open, state, ticketId, jvid]);

  const loadWorkflow = useCallback(async (orderId: number) => {
    setWorkflows((w) => (w[orderId] ? w : { ...w, [orderId]: 'loading' }));
    if (workflows[orderId]) return;
    try {
      const res = await fetchOrderWorkflow(ticketId, orderId);
      setWorkflows((w) => ({ ...w, [orderId]: res.data }));
    } catch {
      setWorkflows((w) => ({ ...w, [orderId]: 'error' }));
    }
  }, [ticketId, workflows]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs">
          <MapPin className="w-3.5 h-3.5" /> {t('tickets:enrichment.open')}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto p-5">
        <SheetTitle className="flex items-center gap-2 text-base">
          <MapPin className="w-4 h-4 text-brand-rose" /> {t('tickets:enrichment.title')}
        </SheetTitle>
        <SheetDescription>{t('tickets:enrichment.subtitle')}</SheetDescription>

        <div className="mt-4 space-y-3">
          {state === 'loading' && (
            <>
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-24 w-full" />
            </>
          )}

          {state === 'error' && (
            <Banner><AlertTriangle className="w-4 h-4" /> {t('tickets:enrichment.loadError')}</Banner>
          )}

          {state === 'ready' && data && !data.available && (
            <p className="text-sm text-slate-400 py-10 text-center">{t('tickets:enrichment.noJvid')}</p>
          )}

          {state === 'ready' && data && data.available && (
            <>
              {data.dmpError && (
                <Banner><AlertTriangle className="w-4 h-4" /> {t('tickets:enrichment.dmpUnavailable')}</Banner>
              )}

              {data.center && (
                <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2 text-sm">
                  <div className="font-semibold text-slate-800">
                    {[data.center.streetName, data.center.houseNumber].filter(Boolean).join(' ') || '—'}
                  </div>
                  <div className="text-xs text-slate-500">
                    {[data.center.postalCode, data.center.city].filter(Boolean).join(' ')} · JVID {data.center.jvid}
                  </div>
                </div>
              )}

              {/* Address / delivery / status */}
              {data.addressStatus && (
                <Section icon={<Radio className="w-4 h-4" />} title={t('tickets:enrichment.addressStatus')} defaultOpen>
                  <KvGrid rows={[
                    [t('tickets:enrichment.f.status'), v(data.addressStatus.status)],
                    [t('tickets:enrichment.f.delivery'), v(data.addressStatus.deliveryStatus)],
                    [t('tickets:enrichment.f.deliveryCategory'), v(data.addressStatus.deliveryStatusCategory)],
                    [t('tickets:enrichment.f.area'), [data.addressStatus.areaName, data.addressStatus.areaId].filter(Boolean).join(' · ') || '—'],
                    [t('tickets:enrichment.f.quadrant'), v(data.addressStatus.quadrant)],
                    [t('tickets:enrichment.f.pop'), v(data.addressStatus.pop)],
                  ]} />
                </Section>
              )}

              {/* Network */}
              {data.network && (
                <Section icon={<Network className="w-4 h-4" />} title={t('tickets:enrichment.network')}>
                  <KvGrid rows={[
                    [t('tickets:enrichment.f.pop'), v(data.network.areaPop)],
                    [t('tickets:enrichment.f.operator'), v(data.network.operator)],
                    [t('tickets:enrichment.f.reference'), v(data.network.customerReference)],
                    [t('tickets:enrichment.f.splitterRack'), v(data.network.splitterRackLabel)],
                    [t('tickets:enrichment.f.splitter'), v(data.network.splitterLabel)],
                    [t('tickets:enrichment.f.splitterStatus'), v(data.network.splitterStatus)],
                    [t('tickets:enrichment.f.port'), [data.network.splitterPortPosition, data.network.splitterPortStatus].filter(Boolean).join(' · ') || '—'],
                    [t('tickets:enrichment.f.patch'), v(data.network.splitterPatchStatus)],
                    [t('tickets:enrichment.f.patchPanel'), v(data.network.patchPanelRackLabel)],
                    [t('tickets:enrichment.f.fiberStatus'), v(data.network.addressFiberStatus)],
                    [t('tickets:enrichment.f.fiberNumber'), v(data.network.addressFiberNumber)],
                    [t('tickets:enrichment.f.utac'), v(data.network.utac)],
                  ]} />
                </Section>
              )}

              {/* Orders + lazy workflow */}
              {data.orders.length > 0 && (
                <Section icon={<Truck className="w-4 h-4" />} title={t('tickets:enrichment.orders')} count={data.orders.length} defaultOpen>
                  <div className="space-y-1.5">
                    {data.orders.map((o) => {
                      const wf = workflows[o.orderId];
                      return (
                        <div key={o.orderId} className="rounded-lg border border-slate-100">
                          <button
                            type="button"
                            onClick={() => loadWorkflow(o.orderId)}
                            className="w-full flex items-center gap-2 px-2.5 py-2 text-left hover:bg-slate-50"
                          >
                            {wf ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                            <span className="text-xs font-semibold text-slate-700">#{o.orderId}</span>
                            <span className="text-xs text-slate-500">{v(o.orderType)}</span>
                            <span className="ml-auto"><StatusBadge status={o.orderStatus || '—'} /></span>
                          </button>
                          <div className="px-2.5 pb-1.5 text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-0.5">
                            <span>{t('tickets:enrichment.f.operator')}: {v(o.operatorName)}</span>
                            <span>{t('tickets:enrichment.f.created')}: {fmtDate(o.createdAt)}</span>
                            {o.technicallyExecutedAt && <span>{t('tickets:enrichment.f.executed')}: {fmtDate(o.technicallyExecutedAt)}</span>}
                            {o.splitterRef && <span>{t('tickets:enrichment.f.splitter')}: {o.splitterRef}</span>}
                          </div>
                          {wf && (
                            <div className="border-t border-slate-100 px-2.5 py-2">
                              {wf === 'loading' && <span className="text-xs text-slate-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> {t('tickets:enrichment.workflowLoading')}</span>}
                              {wf === 'error' && <span className="text-xs text-rose-500">{t('tickets:enrichment.workflowError')}</span>}
                              {Array.isArray(wf) && (wf.length === 0
                                ? <span className="text-xs text-slate-400">{t('tickets:enrichment.workflowNone')}</span>
                                : <ol className="space-y-1">
                                    {wf.map((e, i) => (
                                      <li key={i} className="text-[11px] text-slate-600">
                                        <span className="font-medium">{v(e.fromStatus)} → {e.toStatus}</span>
                                        <span className="text-slate-400"> · {fmtDate(e.updatedAt)}</span>
                                        {e.timeslot && <span className="text-slate-400"> · {e.timeslot}</span>}
                                        {e.delayReason && <span className="text-amber-600"> · {e.delayReason}</span>}
                                        {e.delayReasonRemark && <span className="text-slate-400"> ({e.delayReasonRemark})</span>}
                                      </li>
                                    ))}
                                  </ol>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Section>
              )}

              {/* Subscriptions */}
              {data.subscriptions.length > 0 && (
                <Section icon={<FileText className="w-4 h-4" />} title={t('tickets:enrichment.subscriptions')} count={data.subscriptions.length}>
                  <SimpleTable
                    head={[t('tickets:enrichment.f.operator'), t('tickets:enrichment.f.type'), t('tickets:enrichment.f.identifier'), t('tickets:enrichment.f.sla'), t('tickets:enrichment.f.start'), t('tickets:enrichment.f.end')]}
                    rows={data.subscriptions.map((s) => [v(s.operator), v(s.type), v(s.subscriptionIdentifier), v(s.sla), fmtDate(s.startDate), fmtDate(s.endDate)])}
                  />
                </Section>
              )}

              {/* SSV / TSA */}
              {data.ssvTsa.length > 0 && (
                <Section icon={<FileText className="w-4 h-4" />} title={t('tickets:enrichment.ssvTsa')} count={data.ssvTsa.length}>
                  <SimpleTable
                    head={[t('tickets:enrichment.f.task'), 'SSV', 'TSA']}
                    rows={data.ssvTsa.map((s) => [v(s.task), v(s.ssvStatus), v(s.tsaStatus)])}
                  />
                </Section>
              )}

              {/* Other tickets at this JVID */}
              {data.tickets.length > 0 && (
                <Section icon={<TicketIcon className="w-4 h-4" />} title={t('tickets:enrichment.ticketsAtJvid')} count={data.tickets.length}>
                  <div className="space-y-1">
                    {data.tickets.map((tk) => (
                      <div key={tk.id} className="flex items-center gap-2 text-xs py-1">
                        <span className="font-semibold text-slate-700">{tk.ticketNumber}</span>
                        <span className="text-slate-500 truncate">{tk.subject}</span>
                        <span className="ml-auto shrink-0"><StatusBadge status={tk.status} /></span>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Nearby addresses (building siblings) */}
              {data.nearby.length > 0 && (
                <Section icon={<Building2 className="w-4 h-4" />} title={t('tickets:enrichment.nearby')} count={data.nearby.length}>
                  <SimpleTable
                    head={['JVID', t('tickets:enrichment.f.address'), t('tickets:enrichment.f.city')]}
                    rows={data.nearby.map((a) => [
                      String(a.jvid),
                      [a.streetName, a.houseNumber, a.busNumber].filter(Boolean).join(' ') || '—',
                      [a.postalCode, a.city].filter(Boolean).join(' ') || '—',
                    ])}
                  />
                </Section>
              )}

              <p className="text-[10px] text-slate-300 pt-1 text-center">
                {t('tickets:enrichment.fetchedAt', { when: fmtDate(data.fetchedAt) })}
              </p>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Presentational helpers ────────────────────────────────────

function Section({ title, icon, count, defaultOpen = false, children }: {
  title: string; icon: React.ReactNode; count?: number; defaultOpen?: boolean; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-slate-100">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
      >
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <span className="text-brand-rose">{icon}</span>
        <span className="text-sm font-bold text-slate-800">{title}</span>
        {count != null && (
          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">{count}</span>
        )}
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}

function KvGrid({ rows }: { rows: [string, string][] }) {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
      {rows.map(([k, val], i) => (
        <div key={i} className="contents">
          <span className="text-xs text-slate-400">{k}</span>
          <span className="text-xs font-medium text-slate-700 text-right">{val}</span>
        </div>
      ))}
    </div>
  );
}

function SimpleTable({ head, rows }: { head: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-slate-400 text-left uppercase tracking-wide">
            {head.map((h, i) => <th key={i} className="font-semibold pb-1 pr-3 whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              {r.map((c, j) => <td key={j} className="py-1.5 pr-3 text-slate-600 whitespace-nowrap">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Banner({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
      {children}
    </div>
  );
}
